import { useState, useEffect } from 'react';
import WholesaleLayout, { useWholesaleAuth } from './WholesaleLayout';
import { getWholesaleUsers, createWholesaleUser, getWholesaleProfile, updateWholesaleProfile, lockWholesaleProfile, unlockWholesaleProfile } from '../../lib/api';

export default function WholesaleAdminUsers() {
  const user = useWholesaleAuth('admin');
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ username:'', password:'', display_name:'', company_name:'' });
  const [profileForm, setProfileForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!user) return;
    try {
      const res = await getWholesaleUsers();
      setUsers(res.data);
      const profs = {};
      for (const u of res.data) {
        try { const p = await getWholesaleProfile(u.username); profs[u.username] = p.data; } catch { profs[u.username] = {}; }
      }
      setProfiles(profs);
    } catch {}
  };
  useEffect(() => { load(); }, [user]);

  const handleAdd = async () => {
    if (!form.username || !form.password) { alert('Username and password required'); return; }
    setSaving(true);
    try {
      await createWholesaleUser(form);
      setShowAdd(false); setForm({ username:'', password:'', display_name:'', company_name:'' }); load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const handleEditProfile = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await updateWholesaleProfile(editUser, profileForm);
      setEditUser(null); load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const toggleLock = async (username, locked) => {
    try {
      if (locked) await unlockWholesaleProfile(username);
      else await lockWholesaleProfile(username);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const openEdit = (username) => {
    const p = profiles[username] || {};
    setProfileForm({ company_name: p.company_name||'', contact_person: p.contact_person||'', email: p.email||'', phone: p.phone||'', address: p.address||'', city: p.city||'', zip: p.zip||'', country: p.country||'' });
    setEditUser(username);
  };

  return (
    <WholesaleLayout user={user} title="Wholesale Users">
      <div style={{ marginBottom:20, display:'flex', justifyContent:'flex-end' }}>
        <button onClick={() => setShowAdd(true)} data-testid="add-wholesaler-btn" style={btnPrimary}>Add Wholesaler</button>
      </div>

      <div style={card}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead><tr>{['Username','Display Name','Company','Country','Status','Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {users.map(u => {
              const p = profiles[u.username] || {};
              const locked = p.locked;
              const editReq = p.edit_requested;
              return (
                <tr key={u.username}>
                  <td style={tdStyle}>{u.username}</td>
                  <td style={tdStyle}>{u.display_name}</td>
                  <td style={tdStyle}>{p.company_name || '—'}</td>
                  <td style={tdStyle}>{p.country || '—'}</td>
                  <td style={tdStyle}>
                    {locked ? <span style={{ ...badge, background:'#111', color:'#fff' }}>LOCKED</span>
                     : editReq ? <span style={{ ...badge, background:'#FFF3E0', color:'#E65100' }}>UNLOCK REQ</span>
                     : <span style={{ ...badge, background:'#E8F5E9', color:'#2E7D32' }}>EDITABLE</span>}
                  </td>
                  <td style={tdStyle}>
                    <button onClick={() => toggleLock(u.username, locked)} style={{ ...btnSm, marginRight:6 }}>{locked ? 'Unlock' : 'Lock'}</button>
                    <button onClick={() => openEdit(u.username)} style={btnSm}>Edit Profile</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {users.length === 0 && <div style={{ padding:32, textAlign:'center', color:'#888' }}>No wholesale users yet</div>}
      </div>

      {/* Add modal */}
      {showAdd && (
        <div style={overlay} onClick={() => setShowAdd(false)}>
          <div onClick={e => e.stopPropagation()} style={modal}>
            <div style={cardTitle}>Add Wholesaler</div>
            {[['username','Username'],['password','Password'],['display_name','Display Name'],['company_name','Company Name']].map(([k,l]) => (
              <div key={k} style={{ marginBottom:12 }}>
                <label style={labelStyle}>{l}</label>
                <input value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} type={k==='password'?'password':'text'} style={inputStyle} data-testid={`add-${k}`} />
              </div>
            ))}
            <div style={{ display:'flex', gap:8, marginTop:16 }}>
              <button onClick={handleAdd} disabled={saving} style={btnPrimary}>{saving ? 'Creating...' : 'Create'}</button>
              <button onClick={() => setShowAdd(false)} style={btnGhost}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit profile modal */}
      {editUser && (
        <div style={overlay} onClick={() => setEditUser(null)}>
          <div onClick={e => e.stopPropagation()} style={modal}>
            <div style={cardTitle}>Edit Profile: {editUser}</div>
            {[['company_name','Company Name'],['contact_person','Contact Person'],['email','Email'],['phone','Phone'],['address','Address'],['city','City'],['zip','ZIP'],['country','Country']].map(([k,l]) => (
              <div key={k} style={{ marginBottom:12 }}>
                <label style={labelStyle}>{l}</label>
                <input value={profileForm[k]||''} onChange={e => setProfileForm(f => ({ ...f, [k]: e.target.value }))} style={inputStyle} />
              </div>
            ))}
            <div style={{ display:'flex', gap:8, marginTop:16 }}>
              <button onClick={handleEditProfile} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : 'Save'}</button>
              <button onClick={() => { const p = profiles[editUser]||{}; toggleLock(editUser, p.locked); }} style={btnOutline}>{profiles[editUser]?.locked ? 'Unlock' : 'Lock'}</button>
              <button onClick={() => setEditUser(null)} style={btnGhost}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </WholesaleLayout>
  );
}

const card = { background:'#fff', border:'1px solid #E0DED8', borderRadius:4, padding:24 };
const cardTitle = { fontFamily:"'Barlow Condensed',sans-serif", fontSize:16, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', marginBottom:16 };
const thStyle = { padding:'10px 12px', fontSize:10, letterSpacing:1.5, textTransform:'uppercase', color:'#888', borderBottom:'2px solid #E0DED8', fontWeight:600, textAlign:'left' };
const tdStyle = { padding:'12px', borderBottom:'1px solid #E0DED8', verticalAlign:'middle' };
const badge = { display:'inline-block', padding:'3px 10px', fontSize:10, letterSpacing:1, fontWeight:600, borderRadius:2, textTransform:'uppercase' };
const overlay = { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' };
const modal = { background:'#fff', borderRadius:4, padding:32, width:460, maxWidth:'90vw', maxHeight:'90vh', overflowY:'auto' };
const labelStyle = { display:'block', fontSize:11, letterSpacing:1.5, textTransform:'uppercase', fontWeight:600, color:'#888', marginBottom:6 };
const inputStyle = { width:'100%', padding:'10px 12px', border:'1px solid #E0DED8', borderRadius:2, fontFamily:'Barlow', fontSize:13, outline:'none' };
const btnPrimary = { display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', fontFamily:'Barlow', fontSize:12, fontWeight:600, letterSpacing:1, textTransform:'uppercase', borderRadius:2, cursor:'pointer', background:'#C41E2A', color:'#fff', border:'none' };
const btnOutline = { display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', fontFamily:'Barlow', fontSize:12, fontWeight:600, letterSpacing:1, textTransform:'uppercase', borderRadius:2, cursor:'pointer', background:'transparent', color:'#111', border:'1.5px solid #111' };
const btnGhost = { display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', fontFamily:'Barlow', fontSize:12, fontWeight:600, letterSpacing:1, textTransform:'uppercase', borderRadius:2, cursor:'pointer', background:'transparent', color:'#888', border:'1px solid #E0DED8' };
const btnSm = { display:'inline-flex', alignItems:'center', padding:'5px 10px', fontFamily:'Barlow', fontSize:11, fontWeight:600, letterSpacing:1, textTransform:'uppercase', borderRadius:2, cursor:'pointer', background:'transparent', color:'#111', border:'1px solid #E0DED8' };
